// utils/helpers.ts

import { useSearchParams } from "react-router-dom";
import { Beacon, BeaconEventType, ReferralCode } from "../Entities";
import { notify } from "../components/common/Toast/toastBridge";

/** Format currency in Czech crowns. */
export const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: 'CZK',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

/** Format number with Czech locale. */
export const formatNumber = (number: number): string =>
  new Intl.NumberFormat('cs-CZ').format(number);

/** Format date in Czech locale. */
export const formatDate = (date: string | number | Date): string =>
  new Intl.DateTimeFormat('cs-CZ', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date));

/** Get greeting based on time of day. */
export const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Dobré ráno';
  if (hour < 18) return 'Dobré odpoledne';
  return 'Dobrý večer';
};

/** Calculate percentage change between two values. */
export const calculatePercentageChange = (oldValue: number, newValue: number): number => {
  if (oldValue === 0) return 0;
  const change = ((newValue - oldValue) / oldValue) * 100;
  return Math.round(change * 10) / 10;
};

/** Validate email format. */
export const validateEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/** Generate a random short id. */
export const generateId = (): string => Math.random().toString(36).substring(2, 11);

/** Debounce a function. */
export const debounce = <A extends unknown[]>(
  func: (...args: A) => void,
  wait: number,
): ((...args: A) => void) => {
  let timeout: ReturnType<typeof setTimeout>;
  return function executedFunction(...args: A) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

type MembershipType = 'free' | 'premium' | 'elite';

/** Get membership color. */
export const getMembershipColor = (type: MembershipType | string): string => {
  const colors: Record<MembershipType, string> = {
    free: '#ff6b35',
    premium: '#4ecdc4',
    elite: '#7b68ee',
  };
  return colors[type as MembershipType] || colors.free;
};

/** Check if user can access feature based on membership. */
export const canAccessFeature = (feature: string, membershipType: MembershipType | string): boolean => {
  const features: Record<MembershipType, string[]> = {
    free: ['basic_bonuses', 'basic_dashboard'],
    premium: ['basic_bonuses', 'premium_bonuses', 'basic_dashboard', 'advanced_dashboard', 'tax_reports'],
    elite: ['all'],
  };

  if (membershipType === 'elite') return true;
  return features[membershipType as MembershipType]?.includes(feature) || false;
};

export const deleteUrlParam = (param: string) => {
  const url = new URL(window.location.href);
  url.searchParams.delete(param);
  window.history.replaceState({}, "", url);
};

export const validateReferralCode = async (): Promise<ReferralCode | null> => {

  let referralCode: ReferralCode | null = null;

  const searchParams = new URLSearchParams(window.location.search);
  const referralCodeStr = searchParams.get('referralCode') || localStorage.getItem('referralCode');

  if (referralCodeStr) {
    try {
      referralCode = await ReferralCode.checkValidity(referralCodeStr);

      // Save & clean up params & tell the Beacon — only when referralCode came from the URL.
      if (searchParams.get('referralCode')) {
        localStorage.setItem('referralCode', referralCodeStr);
        deleteUrlParam("referralCode");
        Beacon.addEvent(BeaconEventType.ON_QR_SCAN, { referral_code: referralCodeStr });
      }
    } catch (e: any) {
      if (e?.message === "invalid referral code") {
        deleteUrlParam("referralCode");
        localStorage.removeItem("referralCode");
      }
      console.error(e);
      notify.error(e?.message === 'invalid referral code' ? 'Referral link je neplatný' : 'Nepodařilo se validovat referral code');
    }
  }
  return referralCode;
}